<template lang="pug">
	.ai-music--list.tb-padding
		ListView.full-width(ref="listView", initialView="table", :hideViewsSelection="true", :getTotal="(r) => r.TotalNumberOfRecords", :getItems="(r) => r.Results", :getData="loadAIMusic", :columns="columns",
			:showColumnHeaders="true", :searchVisible="true", :autoFit="true", :bottomPagerVisible="false", :filterRow="true", @row-prepared="onRowPrepared", :keyboardNavigationDisabled="true", :showPageSize="true")
			template(v-slot:item-template="{ data }")
				.flex-row.space-large.justify.flex-1(:key="data.Oid")
					.flex-row.space-large.flex-1
						.flex-row.space-medium.flex-1
							.flex-col.align-center
								.img-holder(@click="playItem(data)")
									img.item-image(:src="data.Metadata.ImageUrl || data.ImageUrl")
									.play-button.flex-row.align-center.center
										fas(:icon="isMusicBusy(data) ? 'spinner' : 'play'", :spin="isMusicBusy(data)")
							.flex-col.space.wrap.flex-1
								.flex-row.space.wrap.align-center
									a(@click="openAIMusic(data)")
										.flex-row.space.align-center
											i.fas.fa-pen-square
											span {{getSongName(data)}}
									.flex-1.justify-end.full-width.align-center
										RatingControl.justify-end(:value.sync="data.Rating", @value-changed="updateItem(data)")
								.flex-row.space(v-if="data.Metadata.Style")
									span {{data.Metadata.Style}}
								.flex-row.space.align-center.justify
									.flex-row.space.align-center.grey
										.badge.font-tiny.small(v-if="data.Metadata.ReqLyricCount > 1") {{$tCaption('Lyrics ver. ')}}{{data.Metadata.LyricNumber}}
										.badge.font-tiny.small(v-if="data.Metadata.ReqSongCount > 0") {{$tCaption('Song #')}}{{data.SongNumber + 1}}
										.badge.font-tiny.small {{`${data.IsFull ? $tCaption('Full song') : `${$tCaption('Part')} #${data.Part}`}`}}
										.approved.badge.success.small.font-tiny(v-if="data.Approved") {{$tCaption('Approved')}}
										fas.grey(v-if="getVocalsIcon(data)", :icon="getVocalsIcon(data)", size="2xs", :title="getVocalsDescription(data)")
			template(v-slot:item-actions="{ data }")
				.item-actions(:data-timestamp="getTimestamp(data)")
					.flex-row.space-large
						DxDropDownButton(:data-source="getActionsForMusic(data)", :dropDownOptions="dropDownOptions", keyExpr="id", displayExpr="text", stylingMode="text", icon="fas fa-ellipsis-vertical", 
							:showArrowIcon="false", @itemClick="(e) => onActionClick(data, e)")
			template(v-slot:duration-template="{ data }")
				span {{$Data.secondsToDisplayValue(data.Duration)}}
			template(v-slot:status-template="{ data, text }")
				.flex-row.space-small.align-center.clickable.hoverable.accent-color(v-if="data.Status === STATUS_ERROR", @click="$notifyMessage(getErrorMessageHtml(data), { title: $tCaption('Error', '@actions'), fullScreen: true })")
					fas(icon="circle-exclamation")
					span {{text}}
				.flex-row.space.align-center(v-else, :class="{ 'warning-colored': [1,3].includes(data.Status) }")
					fas(v-if="[1,3].includes(data.Status)", icon="spinner", :spin="true")
					span {{text}}
			template(slot="left-actions")
				.flex-row.space-medium.align-center
					.font-large {{$tCaption('AI Song')}}
					.flex-row.align-end
						DxButton(icon="plus", ref="newActions", @click="onNewActionsButtonClick", :hint="$tCaption('Actions')")
						.new-actions-context-menu
						DxContextMenu(ref="newActionsContextMenu", :data-source="newItemActions", target=".new-actions-context-menu", :width="200", @item-click="onNewItemActionClick")
		Popup(v-if="editingItem", :popupVisible="true", :title="editingItem.Metadata.Name || $tCaption('Edit AI Song')", :autosize="true", :width="820", :closeOnOutsideClick="false", :showMaximize="true")
			Detail(:item="editingItem")
			template(slot="actions-left")
				DxButton(:text="$tCaption('Delete', '@actions')", @click="deleteAiMusic(editingItem.Oid)", type="danger")
				DxButton(v-if="allowGenerate", :text="regenerateCaption", @click="regenerate()", :disabled="editingItem.Status === STATUS_LYRICS_GENERATING", icon="refresh")
				Loader(v-if="isGenerating", :text="$tCaption('Generating')")
			template(slot="actions-right")
				DxButton(:text="$tCaption('Cancel', '@actions')", @click="editingItem = null")
				DxButton(v-if="editingItem.Status === STATUS_ERROR", :text="$tCaption('Save and regenerate', '@actions')", @click="saveEditingItem(true)", type="success", :disabled="editingItem.Status === STATUS_LYRICS_GENERATING")
				DxButton(:text="$tCaption('Save', '@actions')", @click="saveEditingItem(false)", type="success", :disabled="editingItem.Status === STATUS_LYRICS_GENERATING")
		Loader(v-if="isLoading", text="", :fullscreen="true")
		Popup(v-if="generateSongPopupVisible", :popupVisible.sync="generateSongPopupVisible", :title="$tCaption('Generate song')", :autosize="true", :width="320", :closeOnOutsideClick="true")
			.flex-col.space-small
				.font-small.upper.grey {{$tCaption('Song count')}}
				DxNumberBox(:value.sync="songCount", stylingMode="filled", :showSpinButtons="true", :min="2", :step="2")
			template(slot="actions-right")
				DxButton(:text="$tCaption('Cancel', '@actions')", @click="generateSongPopupVisible = false")
				DxButton(:text="$tCaption('Generate')", @click="confirmGenerateSong()", type="success")
		NewAIMusicForm(ref="newItem", @reload="$refs.listView && $refs.listView.reload()", :isDebug="isDebug")
		SideForm(ref="sidePanel", :waveformVisible="!!playingMusic", :visible="playerVisible")
			SideFormDetail(ref="sideDetail", :item="playingMusic")
			.flex-row.justify(slot="actions")
				.flex-row.align-center.space
					template(v-if="previousClip")
						DxButton(:text="$tCaption('Paste previous info')", icon="fas fa-copy", @click="pastePreviousInfo")
						.grey {{$tCaption('or press (Insert)')}}
				.flex-row
		ContinueAtForm(ref="continueAt", @reload="$refs.listView && $refs.listView.reload()", :waveformVisible="!!playingMusic", :isDebug="isDebug")
		BottomAudioPlayer(ref="player", :visible="playerVisible", :src="playingMusic && (playingMusic.__blob || playingMusic.AudioUrl)", @hide="playingMusic = null", :playState="playState", :title="playingMusic && getSongName(playingMusic, true)",
			@timeline-click="onTimelineClick", :selectionMode="true", @selection="onSelection", :baseActionsVisible="!selection.active", @buffer="v => buffer = v")
			template(slot="actions-left")
				a.justify.nowrap(v-show="!selection.active", @click="nextClip(-1)")
					.flex-row.space.align-center
						fas(icon="chevron-left")
						span {{$tCaption('Prev clip')}}
			template(slot="actions")
				a.nowrap(v-if="selection.active", id="selectionActions")
					.flex-row.space.align-center
						span {{`${$Data.secondsToDisplayValue(selection.range[0])} - ${$Data.secondsToDisplayValue(selection.range[1])}`}}
						fas(icon="angle-down")
				a.justify.nowrap(v-if="selection.active", @click="clearSelection")
					.flex-row.space.align-center
						fas(icon="ban")
						span {{$tCaption('Clear selection')}}
				a.justify.nowrap(v-show="!selection.active", :title="$tCaption('Play last 10 seconds')", @click="playLast10Secs")
					fas(icon="forward-step")
				a.justify.nowrap(v-show="!selection.active", @click="nextClip(1)")
					.flex-row.space.align-center
						span {{$tCaption('Next clip')}}
						fas(icon="chevron-right")
				a.justify.nowrap(v-show="!selection.active", @click="continueFromHere")
					.flex-row.space.align-center
						span {{$tCaption('Extend from here')}}
						fas(icon="arrow-right")
				a.justify.nowrap(v-show="changesHistory.length", @click="undoChanges")
					.flex-row.space.align-center
						fas(icon="rotate-left")
						span {{$tCaption('Undo')}}
				a.justify.nowrap(v-show="changesHistory.length || (playingMusic && playingMusic.__saving)", @click="saveChanges", :class="{ 'disabled-controls': playingMusic && playingMusic.__saving }")
					.flex-row.space.align-center
						fas(:icon="playingMusic && playingMusic.__saving ? 'spinner' : 'cloud-arrow-up'", :spin="playingMusic && playingMusic.__saving")
						span {{$tCaption('Save')}}
		AudioProcessing(:buffer="buffer", :selection="selection", @change="onAudioChanged", target="#selectionActions")
</template>

<script>
import _ from 'lodash';
import ListView from 'app-core/ListView.vue';
import Detail from './Detail.vue';
import DxButton from 'devextreme-vue/button';
import DxTextBox from 'devextreme-vue/text-box';
import DxTextArea from 'devextreme-vue/text-area';
import DxNumberBox from 'devextreme-vue/number-box';
import DxSlider from 'devextreme-vue/slider';
import Popup from 'app-core/Popup.vue';
import Loader from 'app-core/Loader.vue';
import { AI_MUSIC_STATUS, AI_MUSIC_VOCALS } from 'app-core/constants';
import DxDropDownButton from 'devextreme-vue/drop-down-button';
import BottomAudioPlayer from 'view/audioPlayer/BottomAudioPlayer.vue';
import Dropdown from 'app-core/Dropdown.vue';
import ContinueAtForm from './ContinueAtForm.vue';
import audioHelper from 'view/audioTracker/audio-helper';
import NewAIMusicForm from './NewAIMusicForm.vue';
import DxContextMenu from 'devextreme-vue/context-menu';
import RatingControl from 'app-core/RatingControl.vue';
import SideForm from './SideForm.vue';
import SideFormDetail from './SideFormDetail.vue';
import AudioProcessing from './AudioProcessing.vue';

const STATUS_NONE = 0;
const STATUS_LYRICS_GENERATING = 1;
const STATUS_LYRICS_DONE = 2;
const STATUS_MUSIC_GENERATING = 3;
const STATUS_MUSIC_DONE = 4;
const STATUS_ERROR = 5;

export default {
	translationCategory: 'aiMusic.List',
	components: { 
		ListView, DxButton, Popup, Detail, DxTextBox, DxTextArea, Loader, DxDropDownButton, BottomAudioPlayer, Dropdown,
		DxSlider, ContinueAtForm, DxNumberBox, NewAIMusicForm, DxContextMenu, RatingControl, SideForm, SideFormDetail,
		AudioProcessing
	},
	props: [],
	data() {
		return {
			editingItem: null,
			sourceItem: null,
			isLoading: 0,
			statuses: AI_MUSIC_STATUS.toLocale('name', this),
			actions: [
				{ id: 'retry', text: this.$tCaption('Re-try'), icon: 'fas fa-refresh', hidden: (item) => item.Status !== STATUS_ERROR },
				{ id: 'continue', text: this.$tCaption('Extend'), icon: 'fas fa-forward', hidden: (item) => item.IsFull || item.Approved || item.Status < STATUS_MUSIC_DONE || item.Status === STATUS_ERROR },
				{ id: 'approve', text: this.$tCaption('Approve'), icon: 'fas fa-check', hidden: (item) => !item.IsFull || item.Approved || item.Status === STATUS_ERROR },
				{ id: 'generateImage', text: this.$tCaption('Regenerate image'), icon: 'fas fa-refresh', hidden: (item) => !item.Approved },
				{ id: 'generateSong', text: this.$tCaption('Generate song'), icon: 'fas fa-refresh', hidden: (item) => item.Status !== STATUS_LYRICS_DONE },
				{ id: 'endSong', text: this.$tCaption('End song'), icon: 'fas fa-check', hidden: (item) => item.IsFull || item.Approved || item.Status < STATUS_MUSIC_DONE || item.Status === STATUS_ERROR },
				{ id: 'getWholeSong', text: this.$tCaption('Get whole song'), icon: 'fas fa-flag-checkered', hidden: (item) => item.IsFull || item.Approved || item.Status < 3 || item.Status === STATUS_ERROR },
				{ id: 'downloadAudio', text: this.$tCaption('Download audio'), icon: 'fas fa-download', hidden: (item) => !item.AudioUrl },
				{ id: 'getLink', text: this.$tCaption('Get link'), icon: 'fas fa-link', hidden: (item) => !item.AudioUrl },
			],
			dropDownOptions: {
				minWidth: 200
			},
			isDebug: this.$auth.isLocalhost() && __DEBUG__ && true,
			playingMusic: null,
			generatorStatus: null,
			vocals: AI_MUSIC_VOCALS.toLocale('name', this),
			generateSongPopupVisible: false,
			songCount: 2,
			newItemActions: [
				{ id: 'single', text: this.$tCaption('New AI Song'), icon: 'fas fa-plus' },
				{ id: 'batch', text: this.$tCaption('Bach creation'), icon: 'fas fa-clone' },
			],
			previousClip: null,
			playNextClipOnLoad: false,
			STATUS_LYRICS_GENERATING,
			STATUS_LYRICS_DONE,
			STATUS_MUSIC_DONE,
			STATUS_ERROR,
			selection: {
				active: false,
				range: []
			},
			buffer: null,
			playState: false,
			changesHistory: []
		};
	},
	computed: {
		columns() {
			return [
				{ dataField: 'Metadata.Name', caption: () => this.$tCaption('Song'), cellTemplate: 'item-template' },
				{ dataField: 'Duration', caption: () => this.$tCaption('Duration'), width: 100, cellTemplate: 'duration-template' },
				{ dataField: 'Status', caption: () => this.$tCaption('Status'), width: 140, lookup: {
					dataSource: this.statuses,
					displayExpr: 'name',
					valueExpr: 'id'
				}, cellTemplate: 'status-template' },
				{ dataField: 'Rating', caption: () => this.$tCaption('Rating'), width: 70 },
				{ dataField: 'Name', caption: ' ', name: 'Actions', cellTemplate: 'item-actions', width: 70 },
			];
		},
		playingMusicIdx() {
			return (this.$refs.listView?.items || []).findIndex(f => f === this.playingMusic);
		},
		isPlaying() {
			return this.$refs.player.isPlaying;
		},
		gridDeltaHeight() {
			return this.playerVisible ? 140 : 20;
		},
		playerVisible() {
			return !!this.playingMusic && !this.playNextClipOnLoad;
		},
		allowGenerate() {
			return [STATUS_LYRICS_DONE, STATUS_MUSIC_DONE, STATUS_NONE].includes(this.editingItem?.Status);
		},
		isGenerating() {
			return [STATUS_MUSIC_GENERATING, STATUS_LYRICS_GENERATING].includes(this.editingItem?.Status);
		},
		regenerateCaption() {
			if (this.editingItem?.Status === STATUS_MUSIC_DONE) return this.$tCaption('Generate a new song with the same lyrics');
			if (this.allowGenerate) return this.$tCaption('Generate');

			return this.$tCaption('Generating');
		}
	},
	watch: {
		playingMusic(v) {
			this.$checkHeight();
		}
	},
	mounted() {
		if (__DEBUG__)
			window.aiMusicList = this;

		this.$eventsBus.$on('signalr-on-notification', this.signalrOnNotification);

		this.$checkHeight = _.debounce(() => {
			if (this.$refs.listView.innerHeightDelta !== this.gridDeltaHeight) {
				this.$refs.listView.innerHeightDelta = this.gridDeltaHeight;
				this.$refs.listView.calcSizes({ heightDelta: this.gridDeltaHeight });
			}
		}, 500, {
			leading: false,
			trailing: true
		});

		document.addEventListener('keyup', this.onKeyDown);
	},
	destroyed() {
		this.$eventsBus.$off('signalr-on-notification', this.signalrOnNotification);
		delete this.$audioContext;
		document.removeEventListener('keyup', this.onKeyDown);
	},
	methods: {
		async saveChanges() {
			let item = this.playingMusic;
			let blob = item?.__blob;

			if (!blob) return;

			this.changesHistory = [];

			let fileName = `${this.$Data.uuid()}.wav`;
			this.$set(item, '__saving', true);
			try {
				let azureSettings = await this.$Data.getAzureSettings();
				let containerName = azureSettings?.AiMusicContainerName || 'ai-music';
				let azureFile = await this.$azure.uploadBlobWithInfoResponse(fileName, blob, containerName);
				await this.$Data.postExternalApi('aimusic/SaveAudioToMusic', {
					ObjectId: item.Oid,
					AzureAccountOid: azureFile.AzureAccountOid,
					AzureContainer: azureFile.AzureContainer,
					FileName: azureFile.FileName
				});
			} finally {
				this.$set(item, '__saving', false);
			}
		},
		undoChanges() {
			this.$confirm(void 0, this.$tCaption('Undo audio changes?')).then(() => {
				let item = this.changesHistory.slice(-1)[0];
				this.$set(this.playingMusic, '__blob', item.blob);
				this.clearSelection();

				this.changesHistory.splice(this.changesHistory.length - 1, 1);
			});
		},
		clearSelection() {
			if (this.$refs.player) {
				this.$refs.player.clearSelection();
			}
		},
		onAudioChanged(blob) {
			if (__DEBUG__)
				console.debug('OnAudioChanged', { blobSize: blob?.size });

			this.playState = false;

			if (this.playingMusic) {
				this.changesHistory.push({
					blob: this.playingMusic.__blob
				});
				this.$set(this.playingMusic, '__blob', blob);
				this.clearSelection();
			}
		},
		onSelection(active, range) {
			this.selection.active = active;
			this.selection.range = range;
		},
		getTimestamp(data) {
			return data?.Metadata?.ReqGenId ? `${this.$tCaption('Generation ID:')} ${this.$moment(`${data?.Metadata?.ReqGenId}`, 'YYYYMMDDHHmmssffff').format('HH:mm:ss')}` : '';
		},
		getErrorMessageHtml(data) {
			return `${data.Error || ''}${data.Error && data.LogsUrl ? '<br><br>' : ''}${data.LogsUrl ? `<a href="${data.LogsUrl}" target="_blank">Logs</a>` : ''}`;
		},
		getVocalsIcon(data) {
			return ['' , 'mars', 'venus', 'venus-mars', 'music'][data?.Metadata?.Vocals || ''];
		},
		getVocalsDescription(data) {
			return [this.$tCaption('None') , this.$tCaption('Male'), this.$tCaption('Female'), this.$tCaption('Duet'), this.$tCaption('Instrumental')][data?.Metadata?.Vocals || ''];
		},
		isInput() {
			var activeElement = document.activeElement;
			var inputs = ['input', 'select', 'button', 'textarea'];
			var classes = activeElement ? [...activeElement.classList] : [];

			return activeElement && (inputs.indexOf(activeElement.tagName.toLowerCase()) > -1 || classes.includes('jodit-wysiwyg'));
		},
		onKeyDown(e) {
			if (this._isDestroyed || this.isInput() || !this.playerVisible || !this.$refs.player) return;

			if (__DEBUG__)
				console.debug('KeyDown', { e });

			if (e.code === 'Space') {
				this.$refs.player.togglePlaying();
			}

			if (e.code === 'ArrowLeft') {
				this.$refs.player.changePlay(-10);
			}

			if (e.code === 'ArrowRight') {
				this.$refs.player.changePlay(10);
			}

			if (e.code === 'End') {
				this.playLast10Secs();
			}

			if (e.code === 'ArrowUp') {
				this.nextClip(-1);
			}

			if (e.code === 'ArrowDown') {
				this.nextClip(1);
			}

			if (e.code === 'Insert') {
				this.pastePreviousInfo();
			}

			if (e.code === 'KeyI' && this.$refs.sideDetail) {
				this.$refs.sideDetail.setTab('main');
			}

			if (e.code === 'KeyL') {
				this.$refs.sideDetail.setTab('lyrics');
			}
		},
		pastePreviousInfo() {
			this.playingMusic.Metadata.Style = this.previousClip.Metadata.Style;
			this.playingMusic.Metadata.Keywords = this.previousClip.Metadata.Keywords;
			this.playingMusic.Metadata.Vocals = this.previousClip.Metadata.Vocals;

			if (this.previousClip.__stations?.length) {
				let links = this.$refs.sideDetail?.$refs?.stations?.$refs?.links;
				if (!links) return;

				this.previousClip.__stations.map(f => {
					links.linkUnlink(f, true, true);
				});
			}
		},
		onRowPrepared(e) {
			if (e.rowType != 'header' && e?.data?.Oid) {
				e.rowElement.classList.add('ai-music-row');
				e.rowElement.classList.add(`ai-music-row-${e?.data?.Oid}`);

				if (e.data?.Oid === this.playingMusic?.Oid) {
					e.rowElement.classList.add('active');
				}
			}
		},
		onNewActionsButtonClick() {
			this.$refs.newActionsContextMenu.instance.show();
		},
		onNewItemActionClick(e) {
			this.$refs.newItem.newMusicAI({ batch: e.itemData.id === 'batch' });
		},
		onTimelineClick(time) {
			this.$refs.continueAt.continueAt = time;
		},
		confirmGenerateSong() {
			let aiMusic = this.sourceItem;
			if (!aiMusic) return;

			this.generateSongPopupVisible = false;

			aiMusic.Status = 3;
			this.$Data.postExternalApi('AiMusic/Generate', {
				...aiMusic,
				...aiMusic.Metadata,
				Debug: this.isDebug,
				SongCount: this.songCount / 2
			});
		},
		isMusicBusy(data) {
			return (data === this.playingMusic && this.isPlaying) || (data?.LastJob?.Oid && data?.LastJob.Status < 3);
		},
		getActionsForMusic(data) {
			return this.actions.filter(f => !f.hidden || !f.hidden(data));
		},
		continueFromHere() {
			this.onActionClick(this.playingMusic, void 0, { action: 'continue', continueAt: this.$refs.player.$refs.audioSrc.currentTime });
		},
		getSongName(data, forWaveForm) {
			if (forWaveForm) {
				return `${this.$Data.limitText(data.Metadata.Name, 30) || 'AiMusic'} - ${data.Metadata.Artist} (${this.$tCaption('Song #')}${data.SongNumber + 1})`;
			}
			return `${this.$Data.limitText(data.Metadata.Name, 30) || 'AiMusic'} - ${data.Metadata.Artist}`;
		},
		getNextClipIdx(delta) {
			let idx = 0;
			
			if (this.playingMusicIdx > -1) {
				idx = this.playingMusicIdx + delta;
			} else {
				idx = delta > 0 ? 0 : this.$refs.listView.items.length - 1;
			}

			let aiMusic = this.$refs.listView.items[idx];
			while(aiMusic) {
				if (aiMusic?.Duration) {
					return idx;
				} else {
					idx += delta;
					aiMusic = this.$refs.listView.items[idx];
				}
			}
			return idx;
		},
		nextClip(delta) {
			let aiMusic = this.$refs.listView.items[this.getNextClipIdx(delta)];			
			if (aiMusic?.Duration) {
				this.playItem(aiMusic);
			}

			if (__DEBUG__)
				console.debug('Next clip', { aiMusic });

			if (!aiMusic && !this.$refs.listView.isLoading) {
				this.playNextClipOnLoad = delta;
				setImmediate(() => {
					this.$refs.listView.pageIndex += delta;
				});
			}
		},
		playLast10Secs() {
			this.$refs.player.$refs.audioSrc.currentTime = this.$refs.player.$refs.audioSrc.duration - 10;
			this.$refs.player.$refs.waveForm.waveform();
			this.$refs.player.isPlaying = true;
		},
		async playItem(aiMusic) {
			if (this.playingMusic && this.changesHistory?.length) {
				let save = await this.$confirm(void 0, this.$tCaption('Save audio changes?'))
					.then(() => true)
					.catch(() => false);

				if (save) {
					this.saveChanges();
				} else {
					this.playingMusic.__blob = null;
				}
			}

			this.playNextClipOnLoad = null;
			this.previousClip = this.playingMusic || this.previousClip;
			this.playingMusic = null;
			this.changesHistory = [];

			if (!aiMusic?.Metadata?.TempoBpm) {
				this.detectBeat(aiMusic);
			}
			this.$nextTick(() => {
				this.playingMusic = aiMusic;
				this.playState = true;
				let item = this.$el.querySelector('.ai-music-row.active');
				if (item) {
					item.classList.remove('active');
				}
				item = this.$el.querySelector(`.ai-music-row-${aiMusic.Oid}`);
				if (item) {
					item.classList.add('active');
				}

				let preloadClip = (idx) => {
					let nextClip = this.$refs.listView.items[this.getNextClipIdx(idx)];
					if (nextClip && nextClip !== aiMusic && aiMusic?.Duration) {
						nextClip.__preloadAudio();
					}	
				}
				[-2, -1, 1, 2].map(f => preloadClip(f));

				setTimeout(() => this.$refs.player.$el.focus(), 100);
			});
		},
		updateItem(aiMusic) {
			return this.$Data.postExternalApi('AiMusic/CreateUpdate', aiMusic);
		},
		async detectBeat(aiMusic) {
			if (!this.$audioContext) {
				this.$audioContext = new AudioContext();
			}
			let blob = await this.$Data.getBlob(aiMusic.AudioUrl, true);
			let buffer = await this.$Data.getAudioBufferFromBlob(blob, this.$audioContext);
			let beat = await audioHelper.detectBeat(buffer, this.$audioContext);
			let bpm = Math.round(beat?.bpm || 0);

			if (__DEBUG__)
				console.debug('detectBeat', { blob, buffer, beat, bpm });

			aiMusic.Metadata.TempoBpm = bpm;

			this.updateItem(aiMusic);
		},
		updateItemsJob(item) {
			if (!item) return;
			let items = this.$refs.listView.items.filter(f => f.Metadata?.Oid === item.Metadata.Oid && f.Oid !== item.Oid);
			items.map(f => this.$set(f, 'LastJob', item.LastJob));
			items.map(f => this.$set(f, 'Metadata', item.Metadata));
		},
		signalrOnNotification({ eventName, data }) {
			if (eventName === 'AiMusic.StatusChanged') {
				let item = this.$refs.listView.items.find(f => f.Oid === data.Oid);
				if (item) {
					_.merge(item, data);
				}
			}

			if (eventName === 'AIScriptGenerator.Started') {
				this.generatorStatus = data.Name;
			}

			if (eventName === 'PersistentJobEntity.StatusChanged') {
				let item = this.$refs.listView.items.find(f => f.LastJob?.Oid === data?.Oid);
				if (!item) return;
				this.$set(item, 'LastJob', data);
				if (data?.ResultJson && data?.Status === 4) {
					let json = JSON.parse(data.ResultJson);
					if (json?.Data?.ImageUrl) {
						this.$set(item.Metadata, 'ImageUrl', json?.Data?.ImageUrl);
					}
				}
				this.updateItemsJob(item);
			}

			if (eventName === 'AiMusic.GenerationFinished') {
				let firstItem = (data || [])[0];
				if (!firstItem) return;
				
				this.$notifySuccess(this.$tCaption('MUSIC_NAME has been generated').replace(/MUSIC_NAME/gi, firstItem.Metadata.Name));

				let errorItem = (data || []).find(f => f.Error);
				if (errorItem) {
					this.$notifyError(this.$tCaption('Error occured while processing MUSIC_NAME: MUSIC_ERROR').replace(/MUSIC_NAME/gi, errorItem.Metadata.Name).replace(/MUSIC_ERROR/gi, errorItem?.Error));
				}

				let item = this.$refs.listView.items.find(f => f.Metadata?.Oid === firstItem?.Metadata?.Oid);
				if (!item && this.$refs.listView.pageIndex > 0) return;
				this.$refs.listView.reload();
			}
		},
		async onActionClick(aiMusic, e, opts) {
			if (__DEBUG__)
				console.debug('onActionClick', { aiMusic, e });

			let action = e?.itemData?.id || opts?.action;

			switch(action) {
				case 'continue':
					this.$refs.continueAt.show(aiMusic, opts?.continueAt || this.continueAt);
					break;
				case 'getWholeSong':
					this.isLoading++;
					await this.$Data.postExternalApi('AiMusic/GetWholeSong', { Oid: aiMusic.Oid, Debug: this.isDebug }).finally(() => {
						if (this._isDestroyed) return;
						this.isLoading--;
						this.$refs.listView.reload();
					});
					break;
				case 'approve': {
						const result = await this.$Data.postExternalApi('AiMusic/Approve', { id: aiMusic.Oid, type: this.$Data.objectTypes.AiMusic });
						this.$set(aiMusic, 'LastJob', result.LastJob || aiMusic?.LastJob);
						this.$set(aiMusic, 'Approved', result.Approved);
						this.updateItemsJob(aiMusic);
					}
					break;
				case 'generateImage': {
						const result = await this.$Data.postExternalApi('AiMusic/GenerateImage', { id: aiMusic.Oid, type: this.$Data.objectTypes.AiMusic });
						this.$set(aiMusic, 'LastJob', result.LastJob || aiMusic?.LastJob);
						this.updateItemsJob(aiMusic);
					}
					break;
				case 'generateSong': {
						this.generateSongPopupVisible = false;
						this.sourceItem = aiMusic;
						this.$nextTick(() => this.generateSongPopupVisible = true);
					}
					break;
				case 'endSong': {
					this.$refs.continueAt.show(aiMusic, opts?.continueAt || this.continueAt, { isEnd: true });
					break;
				}
				case 'downloadAudio': {
					this.$Data.downloadURI(aiMusic.AudioUrl, aiMusic.AudioUrl.split('/').pop());
					break;
				}
				case 'getLink': {
					navigator.clipboard.writeText(aiMusic.AudioUrl);
					this.$notifySuccess(this.$tCaption('Link copied to Clipboard'));
					break;
				}
				case 'retry': {
					aiMusic.Status = 3;
					await this.$Data.postExternalApi('AiMusic/Generate', {
						...aiMusic.Metadata,
						Oid: aiMusic.Oid,
						Debug: this.isDebug,
						SongCount: 1
					});
				}
			}
		},
		loadAIMusic(pageIndex, limit, opts) {
			let filter = opts?.filter;
			if (opts?.searchString) {
				filter = [["Name", "contains", opts?.searchString], "OR", ["Metadata.Artist", "contains", opts?.searchString], "OR", ["Metadata.Style", "contains", opts?.searchString]];
			}
			let filterCriteria = `${filter && filter.length && `&filterCriteria=${encodeURIComponent(JSON.stringify(filter))}` || ''}`;
			let sorting = `&sorting=[{ selector: 'Created', desc: true }]`;
			if (__DEBUG__)
				console.debug('loadAIMusic', { pageIndex, limit, opts });
			return this.$Data.getExternalApi('AiMusic/List', `page=${pageIndex + 1}${filterCriteria}${sorting}`, limit).then(r => {
				let results = r?.Results || [];
				let metadatas = {

				}
				results.map(f => {
					if (metadatas[f.Metadata.Oid]) {
						f.Metadata = metadatas[f.Metadata.Oid];
					} else {
						metadatas[f.Metadata.Oid] = f.Metadata;
					}

					let savingCounter = 0;
					let save = () => {
						savingCounter++;
						if (savingCounter > 1) return;
						return this.$Data.postExternalApi('AiMusic/CreateUpdate', f).then((r) => {
							_.assign(f.Metadata, r?.Metadata);
							if (savingCounter > 1) {
								savingCounter = 0;
								f.__save();
							} else {
								savingCounter = 0;
							}
						});
					};
					let saveDelayed = _.debounce(save, 2000, {
						leading: false,
						trailing: true
					});

					let lyricsLoaded = false;
					let ensureLyricsLoaded = async () => {
						if (!lyricsLoaded) {
							lyricsLoaded = true;

							this.isLoading++;
							await this.$Data.getExternalApi('AiMusic/Get', `id=${f.Oid}`).then(r => {
								this.$set(f.Metadata, 'Lyric', r.Metadata?.Lyric);
							}).finally(() => this.isLoading--);
						}
					};

					Object.defineProperty(f, '__save', { get: () => save });
					Object.defineProperty(f, '__saveDelayed', { get: () => saveDelayed });
					Object.defineProperty(f, '__ensureLoaded', { get: () => ensureLyricsLoaded });

					let htmlAudio = null;
					let preloadAudio = () => {
						if (!htmlAudio && f.AudioUrl) {
							htmlAudio = new Audio();
							htmlAudio.preload = 'auto';
							htmlAudio.src = f.AudioUrl;

							if (__DEBUG__)
								console.debug('Preload');
						}
					}
					Object.defineProperty(f, '__preloadAudio', { get: () => preloadAudio });
				});

				return {
					...r,
					Results: _.orderBy(results, [f => results.find(i => i.Metadata.Oid === f.Metadata.Oid)?.Created, 'SongNumber'], ['desc', 'asc'])
				}
			}).finally(() => {
				setTimeout(() => {
					if (this.playNextClipOnLoad) {
						this.nextClip(this.playNextClipOnLoad);						
						var scroll = this.$refs?.listView?.$refs?.table?.instance?.getScrollable();
						if (scroll) {
							scroll.scrollBy(this.playNextClipOnLoad * 9999 * -1);
						}
						this.playNextClipOnLoad = null;
					}
				}, 500);
			});
		},
		openAIMusic(item) {
			this.editingItem = null;
			this.$nextTick(async () => {
				this.isLoading++;
				this.editingItem = await this.$Data.getExternalApi('AiMusic/Get', `id=${item.Oid}`).finally(() => this.isLoading--);
				this.sourceItem = item;
			});
		},
		async saveEditingItem(regenerate) {
			let item = this.editingItem;
			this.editingItem = null;
			if (this.sourceItem) {
				_.assign(this.sourceItem.Metadata, item.Metadata);
			}
			this.isLoading++;
			await this.$Data.postExternalApi('AiMusic/CreateUpdate', item).finally(async () => {
				if (this._isDestroyed) return;

				if (regenerate) {
					item.Status = 3;
					await this.$Data.postExternalApi('AiMusic/Generate', {
						...item.Metadata,
						Oid: item.Oid,
						Debug: this.isDebug,
						SongCount: 1,
						Instrumental: item?.Metadata?.Vocals === 4
					});
				}

				this.isLoading--;
				this.$refs.listView.reload();
			});
		},
		async regenerate() {
			let item = this.editingItem;
			this.editingItem = null;
			
			this.isLoading++;
			await this.$Data.postExternalApi('AiMusic/Generate', {
				...item.Metadata,
				Oid: item.Status < STATUS_MUSIC_GENERATING ? item.Oid : null,
				Debug: this.isDebug,
				SongCount: 1,
				Instrumental: item?.Metadata?.Vocals === 4
			}).finally(async () => {
				if (this._isDestroyed) return;

				this.isLoading--;
				this.$refs.listView.reload();
			});
		},
		deleteAiMusic(id) {
			this.$confirm(void 0, this.$tCaption('Do you want to delte AI Song part?')).then(() => {
				this.editingItem = null;
				this.isLoading++;
				this.$Data.postExternalApi('AiMusic/Delete', [id]).finally(() => {
					if (this._isDestroyed) return;
					this.isLoading--;
					this.$refs.listView.reload();
				});
			});
		}
	}
};
</script>
<style lang="scss">
	@import "main/themes/generated/variables.base.scss";
	.ai-music--list {
		.img-holder {
			position: relative;
			cursor: pointer;

			.item-image {
				height: 60px;
				border-radius: 4px;
			}

			.play-button {
				position: absolute;
				top: 0px;
				width: 100%;
				height: 100%;
				left: 0px;
				color: rgba(255, 255, 255, 0.85);

				&:hover {
					color: $base-accent;
				}
			}
		}

		.ai-music-row {
			position: relative;

			&.active {
				background: #464653;
				color: #f1f1f1;
			}
		}

		.item-actions {
			&:after {
				content: attr(data-timestamp);
				position: absolute;
				right: 12px;
				top: 4px;
				font-size: 10px;
				color: #b1b1b1;
				pointer-events: none;
			}
		}
	}

	.ai-music-button {
		background: #e7e7e7;
		border-radius: 4px;
		height: 36px;
		width: 34px;
	}
</style>
